import { createCalendarEventSchema, EVENT_TYPES } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, errorMessage, primaryButton, selectClass } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

interface CalendarEvent {
  id: string;
  title: string;
  eventType: (typeof EVENT_TYPES)[number];
  startDate: string;
  endDate: string;
}

export function CalendarTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ title: '', eventType: 'event', startDate: '', endDate: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const events = useQuery({
    queryKey: ['calendar', schoolId],
    queryFn: () => api<CalendarEvent[]>(`/schools/${schoolId}/calendar-events`),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['calendar', schoolId] });
  const create = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/calendar-events`, { method: 'POST', body }),
    onSuccess: () => {
      setForm({ title: '', eventType: 'event', startDate: '', endDate: '' });
      setMessage(null);
      void refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/calendar-events/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => setMessage(errorMessage(e)),
  });

  return (
    <section aria-label={t('acad.tab.calendar')} className="space-y-4">
      {message && <Notice kind="error">{message}</Notice>}
      {can('calendar.manage', schoolId) && (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const candidate = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
            const parsed = createCalendarEventSchema.safeParse(candidate);
            if (!parsed.success)
              return setErrors(
                Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
              );
            setErrors({});
            create.mutate(parsed.data);
          }}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-5"
        >
          <TextField
            label={t('acad.eventTitle')}
            value={form.title}
            error={errors.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div>
            <label htmlFor="event-type" className="block text-sm font-medium">
              {t('acad.type')}
            </label>
            <select
              id="event-type"
              value={form.eventType}
              onChange={(e) => setForm({ ...form, eventType: e.target.value })}
              className={selectClass}
            >
              {EVENT_TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`acad.${x}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <TextField
            label={t('acad.start')}
            type="date"
            value={form.startDate}
            error={errors.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
          />
          <TextField
            label={t('acad.end')}
            type="date"
            value={form.endDate}
            error={errors.endDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
          />
          <div className="self-end">
            <button type="submit" className={primaryButton}>
              {t('acad.addEvent')}
            </button>
          </div>
        </form>
      )}
      {events.data?.length === 0 && <p className="text-slate-600">{t('acad.noEvents')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {events.data?.map((ev) => (
          <li key={ev.id} className="flex items-center justify-between gap-2 p-3">
            <span>
              <span className="font-medium">{ev.title}</span>{' '}
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">
                {t(`acad.${ev.eventType}` as MessageKey)}
              </span>{' '}
              <span className="text-slate-600">
                {ev.startDate === ev.endDate ? ev.startDate : `${ev.startDate} → ${ev.endDate}`}
              </span>
            </span>
            {can('calendar.manage', schoolId) && (
              <button
                type="button"
                className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                onClick={() =>
                  window.confirm(t('common.confirmDelete', { name: ev.title })) &&
                  remove.mutate(ev.id)
                }
              >
                {t('acad.delete')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
